/* eslint-disable react/require-default-props */
import { useEffect, useMemo, useState } from "react";
import { startCase } from "lodash";
import capitalize from "capitalize";
import PropTypes from "prop-types";
import AiAssistant from "@shared/AiAssistant";
import dayjs from "@shared/dayjs";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";
import DateSelector from "@shared/v2/DateSelector/DateSelector";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import FilterPill from "@shared/v2/FilterPill";
import IconButton from "@shared/v2/IconButton";
import Modal from "@shared/v2/Modal";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/v2/Popover";
import TextInput from "@shared/v2/TextInput";
import Toggle from "@shared/v2/Toggle";
import { CalendarDaysSolidV6, ClockFiveRegularV6, Plus, XmarkSolidV6 } from "@shared/v2/Icomoon";
import TextToggle from "@shared/v2/TextToggle";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import OutcomeSelection from "./OutcomeSelection";
import TaskFor from "./TaskFor";
import { createTaskRequest, parseTaskFormErrors, updateTaskRequest } from "./taskFormApi";
import { useAccountMilestoneNames, useCurrentUser } from "../../reducers/layoutReducer/selectors";
import Tooltip from "@shared/v2/Tooltip";

let cachedAgentPromise = null;

const AGREEMENT_OUTCOMES = {
  "Signed - Buyer Agreement": "buyer",
  "Signed - Listing Agreement": "seller",
  "Signed - Both Buyer/Listing": "both",
};

const CONTINGENCY_OPTIONS = [
  { value: "after", label: "After" },
  { value: "before", label: "Before" },
];

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const RECURRING_OPTIONS = [
  { key: "custom", value: "number", label: "Every _ Days" },
  { key: "weekly-specific-day", value: "weekly", label: "Day of Week" },
  { key: "daily", value: "number", label: "Daily", recurringEverySoManyDays: 1 },
  { key: "weekly", value: "number", label: "Weekly", recurringEverySoManyDays: 7 },
  { key: "biweekly", value: "number", label: "Biweekly", recurringEverySoManyDays: 14 },
  { key: "monthly", value: "number", label: "Monthly", recurringEverySoManyDays: 30 },
  { key: "every-3-months", value: "number", label: "Every 3 Months", recurringEverySoManyDays: 90 },
  { key: "every-6-months", value: "number", label: "Every 6 Months", recurringEverySoManyDays: 180 },
  { key: "yearly", value: "number", label: "Yearly", recurringEverySoManyDays: 365 },
];

const recurringOptionKeyForTask = (task) => {
  if (task.recurring_frequency === "weekly") return "weekly-specific-day";

  const matchingPreset = RECURRING_OPTIONS.find(
    (option) =>
      option.value === "number" && option.recurringEverySoManyDays === task.recurring_every_so_many_days,
  );

  return matchingPreset?.key || "custom";
};

const RECURRING_DOW_OPTIONS = [
  { value: 0, label: "On Sunday" },
  { value: 1, label: "On Monday" },
  { value: 2, label: "On Tuesday" },
  { value: 3, label: "On Wednesday" },
  { value: 4, label: "On Thursday" },
  { value: 5, label: "On Friday" },
  { value: 6, label: "On Saturday" },
];

const DATE_PRESET_OPTIONS = [
  { label: "Today", daysFromNow: 0 },
  { label: "Tomorrow", daysFromNow: 1 },
  { label: "7 Days", daysFromNow: 7 },
  { label: "30 Days", daysFromNow: 30 },
  { label: "90 Days", daysFromNow: 90 },
];

const TASK_TIME_OPTIONS = Array.from({ length: 96 }, (_item, index) => {
  const timeLabel = dayjs()
    .startOf("day")
    .add(index * 15, "minutes")
    .format("hh:mm A");
  return { label: timeLabel, value: timeLabel };
});

const TASK_EASY_TIME_OPTIONS = [
  { key: "any", label: "Any Time", value: null },
  { key: "morning", label: "Morning", subLabel: "8 am", value: "08:00 AM" },
  { key: "afternoon", label: "Afternoon", subLabel: "12 pm", value: "12:00 PM" },
  { key: "evening", label: "Evening", subLabel: "5 pm", value: "05:00 PM" },
];

const TASK_EASY_TIME_VALUE_TO_KEY = {
  "08:00 AM": "morning",
  "12:00 PM": "afternoon",
  "05:00 PM": "evening",
};

const TaskTimePillControl = ({ componentProps, children }) => {
  const label = componentProps.selectProps.customPillLabel || "Custom";
  const isOpen = componentProps.selectProps.menuIsOpen;
  const hasCustomTime = componentProps.selectProps.isCustomTimeSelected;

  return (
    <Dropdown.Control
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...componentProps}
      className="!tw-bg-transparent !tw-border-none !tw-shadow-none !tw-min-h-0 !tw-p-0 tw-cursor-pointer"
    >
      <div className="tw-pointer-events-none">
        <FilterPill size="medium" variant="primary" selected={isOpen || hasCustomTime}>
          <span className="tw-inline-flex tw-items-center tw-gap-[6px]">
            {label}
            <ClockFiveRegularV6 size="l" />
          </span>
        </FilterPill>
      </div>
      <div className="tw-w-0 tw-h-0 tw-overflow-hidden">{children}</div>
    </Dropdown.Control>
  );
};

TaskTimePillControl.propTypes = {
  componentProps: PropTypes.shape({
    selectProps: PropTypes.shape({
      menuIsOpen: PropTypes.bool,
      customPillLabel: PropTypes.string,
      isCustomTimeSelected: PropTypes.bool,
    }),
  }).isRequired,
  children: PropTypes.node,
};

TaskTimePillControl.defaultProps = {
  children: null,
};

const APPOINTMENT_TYPE_OPTION_GROUPS = [
  {
    label: "Real Estate",
    options: [
      "Buyer Consultation",
      "Listing Consultation",
      "Buyer/Listing Consultation",
      "Showing Appointment",
      "Client Meeting",
      "General",
      "Follow Up",
    ].map((v) => ({ value: v, label: v })),
  },
  {
    label: "Recruiting",
    options: [
      "Meet & Greet",
      "Screening",
      "Recruiting Appointment",
      "Signing Appointment",
      "Recruiting Follow Up",
    ].map((v) => ({ value: v, label: v })),
  },
];

export const defaultTask = {
  name: "",
  id: null,
  priority: 9,
  visibility: "",
  instructions: "",
  account_id: "",
  note: "",
  assigned_to_user_id: "",
  contingency_rule: {},
  contingency_event_and_type: [],
  taskable_id: "",
  taskable_type: "",
  taskable_name: "",
  appointment_type: "",
  outcome: "",
  location: "",
  externally_visible: false,
  contingent: false,
  recurring: false,
  showInstructions: false,
  showNotes: false,
  completed: null,
  user_id: "",
};

const clientFinder = (agents, clientUuid) => {
  const agent = agents?.find((item) => item.value === clientUuid);
  return agent || "";
};

const returnAppointmentType = (appointmentType) => {
  const simpleAppointments = [
    "General",
    "Follow Up",
    "Client Meeting",
    "Buyer/Listing Consultation",
    "Recruiting Appointment",
  ];

  return simpleAppointments.includes(appointmentType) ? "Appointment" : appointmentType;
};

const getTaskButtonText = ({ task = {}, isEmbedded }) => {
  if (isEmbedded) {
    return `${task.id ? "Update" : "Create"} ${task.type === "Appointment" ? "Appointment" : "Task"}`;
  }
  return task.id ? "Save" : "Create";
};
const getTaskTitle = ({ task = {} }) =>
  `${task.id ? "Edit" : "Add"} ${task.type === "Appointment" ? "Appointment" : "Task"} ${task.taskable_name ? `for ${task.taskable_name}` : ""}`;

const TaskForm = ({
  agreementCallBack,
  contactEmail = "",
  editTaskFor = false,
  getCardTasks,
  handleTaskChange: externalTaskChange,
  hideRecurring = false,
  hideCancel = false,
  isCreatingAppointment = false,
  listingUuid,
  onClose,
  person,
  showHeader = false,
  showTaskFor = false,
  task: inputTask = {},
  taskDetails = false,
  taskFor,
  taskable,
  updateTask: updateTaskCallback,
  v4TaskApi = false,
  isEmbedded = false,
}) => {
  const accountMilestoneNames = useAccountMilestoneNames();
  const currentUser = useCurrentUser();
  const accountUuid = currentUser.account.uuid;
  const taskableData = taskable || {};

  const {
    id,
    assigned_to_user_id: assignedToUserId,
    completed = false,
    contingent,
    instructions,
    location = "",
    note,
    outcome = "",
    priority,
    recurring,
    type,
    user_id: userId = "",
  } = inputTask;

  const appointmentTaskForPerson = isCreatingAppointment
    ? {
        taskable_type: "Person",
        taskable_id: person?.uuid || person?.data?.attributes?.uuid || taskableData?.uuid,
        taskable_name: person?.name
          ? person.name
          : `${person?.first_name || person?.data?.attributes?.first_name || ""} ${
              person?.last_name || person?.data?.attributes?.last_name || ""
            }`.trim() || "No Name",
      }
    : {};

  const [task, setTask] = useState(() => {
    const initialTask = {
      ...defaultTask,
      appointment_type: taskableData.appointment_type || null,
      taskable_id: taskFor?.uuid || taskableData?.uuid || "",
      taskable_type: taskableData.taskable_type || taskFor?.object_type || "",
      taskable_name: taskableData.task_name || taskFor?.name || "",
      outcome: taskableData.outcome || "",
      location: taskableData.location || "",
      showInstructions: instructions,
      showNotes: note,
      user_id: userId || "",
      due_on: dayjs().format("YYYY-MM-DD"),
      calculated_due_on: dayjs().format("YYYY-MM-DD"),
      scheduled_time: dayjs().add(1, "hours").startOf("hour").unix(),
    };

    return {
      ...initialTask,
      ...inputTask,
      assigned_to_user_id: assignedToUserId || (id ? "anyone" : currentUser?.uuid),
      priority: priority || 9,
      account_id: accountUuid,
      outcome,
      location,
      completed: inputTask.state === "completed" ? true : completed,
      user_id: userId || currentUser?.uuid,
      type: isCreatingAppointment ? "Appointment" : type,
      scheduled_time:
        inputTask.scheduled_time || isCreatingAppointment || type === "Appointment"
          ? inputTask.scheduled_time || initialTask.scheduled_time
          : null,
      ...appointmentTaskForPerson,
    };
  });
  const [addTo, setAddTo] = useState(false);
  const [showRecurring, setShowRecurring] = useState(recurring);
  const [showContingency, setShowContingency] = useState(contingent);
  const [disabledButton, setDisabledButton] = useState(false);
  const [disableCompletedCheckbox, setDisableCompletedCheckbox] = useState(false);
  const [errors, setErrors] = useState();
  const [agents, setAgents] = useState();
  const [isCustomDatePickerOpen, setIsCustomDatePickerOpen] = useState(false);
  const [selectedRecurringOptionKey, setSelectedRecurringOptionKey] = useState(() =>
    recurringOptionKeyForTask(task),
  );

  const patchTask = (patch) => {
    setTask((prevTask) => ({
      ...prevTask,
      ...patch,
    }));
  };

  const dispatchTaskEvents = ({ includeTaskUpdate = true } = {}) => {
    if (includeTaskUpdate) {
      window.dispatchEvent(new Event("task:update"));
    }
    window.dispatchEvent(new Event("task:count_change"));
    window.dispatchEvent(new Event("task:polymoprhic_count_change"));
  };

  const handleAssignedToUser = () => {
    patchTask({ assigned_to_user_id: null });
  };

  const handleRecurrence = (key, value) => {
    patchTask({
      recurring_every_so_many_days: key === "recurring_every_so_many_days" ? value : null,
      recurring_day_of_week: key === "recurring_day_of_week" ? value : null,
    });
  };

  const handleTaskChange = (e) => {
    const key = e.target.name;
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    if (key === "recurring_every_so_many_days" || key === "recurring_day_of_week") {
      handleRecurrence(key, value);
      return;
    }

    if (key === "assigned_to_user_id" && value === "anyone") {
      handleAssignedToUser();
      return;
    }

    if (key === "type") {
      if (value === "Appointment") {
        patchTask({
          scheduled_time:
            task.scheduled_time ||
            (task.due_on ? dayjs(task.due_on, "YYYY-MM-DD", true) : dayjs())
              .set("hour", dayjs().hour() + 1)
              .startOf("hour")
              .unix(),
        });
      } else {
        patchTask({ scheduled_time: null });
      }
    }

    patchTask({ [key]: value });
  };

  const handleRecurringFrequencyChange = (option) => {
    const nextFrequency = option?.value;

    if (!nextFrequency) return;

    if (nextFrequency === "weekly") {
      setSelectedRecurringOptionKey(option.key);
      patchTask({
        recurring_frequency: "weekly",
        recurring_every_so_many_days: null,
        recurring_day_of_week: task.recurring_day_of_week ?? 0,
      });
      return;
    }

    setSelectedRecurringOptionKey(option.key);
    patchTask({
      recurring_frequency: nextFrequency,
      recurring_every_so_many_days: option.recurringEverySoManyDays ?? task.recurring_every_so_many_days ?? 1,
      recurring_day_of_week: null,
    });
  };

  const handleGoogleCalendar = (taskData, agentsData, user, email, addToCalendar) => {
    if (!addToCalendar) return;

    const taskSummary = taskData.name;
    const taskLocation = taskData.location;
    const scheduledTime = taskData.scheduled_time;
    const clientUuid = taskData.assigned_to_user_id;
    const agentEmail = user ? encodeURIComponent(user.email) : "";
    const appointmentType = taskData.appointment_type;
    const taskableName = taskData.taskable_name?.length ? taskData.taskable_name : "";
    const scheduledDateTimeStart = dayjs.unix(scheduledTime).format("YYYYMMDDTHHmmSSZ");
    const scheduledDateTimeEnd = dayjs.unix(scheduledTime).add(0.5, "hour").format("YYYYMMDDTHHmmSSZ");
    const scheduledDateTimeEnglishFormatStart = dayjs.unix(scheduledTime).format("ddd, MMM D, YYYY h:mm A");
    const currentTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const client = clientFinder(agentsData, clientUuid);
    const clientEmail = encodeURIComponent(client.email);
    const title = `${returnAppointmentType(appointmentType)}: ${taskableName} And ${client.label}`;
    const sendableEmail = encodeURIComponent(email || taskData.taskable_email);
    const emails = [clientEmail, agentEmail, sendableEmail].filter(Boolean).join(",");

    window.open(
      `https://calendar.google.com/calendar/u/0/r/eventedit?text=${title}&location=${
        taskLocation || "Location"
      }&details=Details:+${taskSummary}%0A%0ADate+and+Time:+${scheduledDateTimeEnglishFormatStart}%0A%0AGuests:%0A+${
        client.label
      }: ${clientEmail}%0A+${taskableName}${
        `: ${sendableEmail}` || ""
      }+&add&dates=${scheduledDateTimeStart}/${scheduledDateTimeEnd}&add=${emails}&ctz=${currentTz}`,
      "_blank",
    );
  };

  const createTask = async (
    taskData,
    taskListingUuid,
    taskAgents,
    user,
    taskContactEmail,
    addToCalendar,
    handleAgreementCallback,
  ) => {
    setDisabledButton(true);
    setErrors("");

    try {
      const responseData = await createTaskRequest({
        taskData,
        listingUuid: taskListingUuid,
        v4TaskApi,
      });
      handleGoogleCalendar(taskData, taskAgents, user, taskContactEmail, addToCalendar);
      dispatchTaskEvents({ includeTaskUpdate: true });
      setDisabledButton(false);
      onClose?.(responseData);
      handleAgreementCallback?.();
      return responseData;
    } catch (err) {
      setDisabledButton(false);
      setErrors(parseTaskFormErrors(err));
      return null;
    }
  };

  const updateTask = async (
    taskData,
    taskListingUuid,
    bulkOperation,
    taskAgents,
    user,
    taskContactEmail,
    addToCalendar,
  ) => {
    const isBulkOperation = Boolean(bulkOperation);

    setDisabledButton(true);

    try {
      const responseData = await updateTaskRequest({
        taskData,
        listingUuid: taskListingUuid,
        v4TaskApi,
      });
      handleGoogleCalendar(taskData, taskAgents, user, taskContactEmail, addToCalendar);
      dispatchTaskEvents({ includeTaskUpdate: !isBulkOperation });
      setDisabledButton(false);
      onClose?.(responseData);
      return responseData;
    } catch (err) {
      setDisabledButton(false);
      setErrors(parseTaskFormErrors(err));
      throw err;
    }
  };

  const handleContingencyRuleChange = (e) => {
    const rule = { [e.target.name]: e.target.value };
    setTask((prevTask) => ({ ...prevTask, contingency_rule: { ...prevTask.contingency_rule, ...rule } }));
  };

  const handleDueOnChange = (value) => {
    const includesTime = /am|pm/i.test(value);
    const parseFormat = includesTime ? "MM/DD/YYYY hh:mm A" : "MM/DD/YYYY";
    const dayjsValue = dayjs(value, parseFormat);
    const existingTaskTime = task.scheduled_time ? dayjs.unix(task.scheduled_time).format("hh:mm A") : null;

    let appliedDayjs = dayjsValue;
    let scheduledTime = null;

    if (includesTime) {
      scheduledTime = dayjsValue.unix();
    } else if (existingTaskTime) {
      appliedDayjs = dayjs(`${dayjsValue.format("MM/DD/YYYY")} ${existingTaskTime}`, "MM/DD/YYYY hh:mm A");
      scheduledTime = appliedDayjs.unix();
    }

    let dueness = "overdue";
    if (appliedDayjs.isSame(new Date(), "day")) {
      dueness = "due_today";
    } else if (appliedDayjs.isAfter(new Date())) {
      dueness = "upcoming";
    }

    patchTask({
      due_on: dayjsValue.format("YYYY-MM-DD"),
      calculated_due_on: dayjsValue.format("YYYY-MM-DD"),
      dueness,
      scheduled_time: scheduledTime,
    });
    // dayjs returns seconds on unix(), Date uses miliseconds
    setDisableCompletedCheckbox(appliedDayjs.unix() * 1000 > Date.now());
  };

  const applyTaskTime = (timeValue) => {
    const dueDayjs = dayjs(task.due_on, "YYYY-MM-DD", true);
    if (!dueDayjs.isValid()) return;

    const normalizedTimeValue = task.type === "Appointment" && !timeValue ? "09:00 AM" : timeValue;

    const dueWithTime = normalizedTimeValue
      ? dayjs(`${dueDayjs.format("MM/DD/YYYY")} ${normalizedTimeValue}`, "MM/DD/YYYY hh:mm A")
      : dueDayjs;

    let dueness = "overdue";
    if (dueWithTime.isSame(new Date(), "day")) {
      dueness = "due_today";
    } else if (dueWithTime.isAfter(new Date())) {
      dueness = "upcoming";
    }

    patchTask({
      due_on: dueDayjs.format("YYYY-MM-DD"),
      calculated_due_on: dueDayjs.format("YYYY-MM-DD"),
      scheduled_time: normalizedTimeValue ? dueWithTime.unix() : null,
      dueness,
    });

    setDisableCompletedCheckbox(dueWithTime.unix() * 1000 > Date.now());
  };

  const handleTaskTimeChange = (option) => {
    const selectedTime = option?.value;
    if (!selectedTime) return;
    applyTaskTime(selectedTime);
  };

  const handleEasyTimeSelect = (timeValue) => {
    applyTaskTime(timeValue);
  };

  const handleAgreementCallback = () => {
    if (AGREEMENT_OUTCOMES[task.outcome]) {
      agreementCallBack?.(AGREEMENT_OUTCOMES[task.outcome]);
    }
  };

  const handleSubmit = async () => {
    const taskData = { ...task };
    if (task.completed) taskData.completed_by_user_id = currentUser?.uuid;

    if (task.type === "Appointment" && !task.appointment_type) {
      setErrors(["Appointment Type is required"]);
      return;
    }

    if (!task.taskable_id) {
      setErrors(["Must select person, transaction or project"]);
      return;
    }

    // Validate date and time requirements (skip for contingent tasks)
    if (!task.contingent) {
      if (task.type === "Appointment") {
        // Appointments require both date and time
        if (!task.due_on) {
          setErrors(["Date is required for appointments"]);
          return;
        }
        if (!task.scheduled_time) {
          setErrors(["Time is required for appointments"]);
          return;
        }
      } else {
        // Standard tasks require date but time is optional
        if (!task.due_on) {
          setErrors(["Date is required"]);
          return;
        }
      }
    }

    if (task.id) {
      try {
        await updateTask(taskData, listingUuid, false, agents, currentUser, contactEmail, addTo);
        updateTaskCallback?.(taskData);
        handleAgreementCallback();
      } catch {
        // updateTask already captures and surfaces errors to the UI.
      }
      return;
    }

    const createdTask = await createTask(
      taskData,
      listingUuid,
      agents,
      currentUser,
      contactEmail,
      addTo,
      handleAgreementCallback,
    );
    if (createdTask) {
      getCardTasks?.(task.taskable_id);
      updateTaskCallback?.(createdTask);
    }
  };

  const toggleCompleteTask = () => {
    setTask((prevTask) => ({
      ...prevTask,
      completed: prevTask.completed ? undefined : true,
      outcome: prevTask.completed ? undefined : prevTask.outcome,
    }));
  };

  const toggleInstructions = () => {
    setTask((prevTask) => ({
      ...prevTask,
      showInstructions: !prevTask.showInstructions,
      instructions: null,
    }));
  };

  const toggleNotes = () => {
    setTask((prevTask) => ({
      ...prevTask,
      showNotes: !prevTask.showNotes,
      note: null,
    }));
  };

  const toggleContingent = () => {
    setShowContingency((prevShowContingency) => {
      const isEnablingContingency = !prevShowContingency;
      setTask((prevTask) => ({
        ...prevTask,
        contingent: isEnablingContingency,
        due_on: isEnablingContingency ? "" : prevTask.due_on,
        calculated_due_on: isEnablingContingency ? "" : prevTask.calculated_due_on,
        dueness: isEnablingContingency ? null : prevTask.dueness,
        contingency_rule: prevShowContingency
          ? {}
          : {
              days: 1,
              position: "after",
            },
      }));

      if (isEnablingContingency) {
        setIsCustomDatePickerOpen(false);
      }

      return isEnablingContingency;
    });
  };

  const toggleRecurring = () => {
    setShowRecurring((prevShowRecurring) => {
      setTask((prevTask) => ({
        ...prevTask,
        recurring: !prevShowRecurring,
        recurring_frequency: "number",
        recurring_every_so_many_days: 1,
      }));
      setSelectedRecurringOptionKey("daily");

      return !prevShowRecurring;
    });
  };

  const taskableOptionsHash = useMemo(() => {
    const milestonableMilestoneOptions = (milestones) =>
      milestones.map((milestone) => ({
        value: `milestone-${milestone}`,
        label: milestone
          .split("_")
          .map((element) => capitalize(element))
          .join(" "),
      }));

    return {
      Person: milestonableMilestoneOptions(accountMilestoneNames.person),
      Listing: milestonableMilestoneOptions(accountMilestoneNames.listing),
      Project: milestonableMilestoneOptions(accountMilestoneNames.project),
    };
  }, [accountMilestoneNames]);

  const taskableOptions = (taskableType) => {
    const actualType = /Listing/.test(taskableType) ? "Listing" : taskableType;
    const taskOptions = taskableOptionsHash[actualType];
    if (task.contingency_event_and_type.length === 0 || task.contingency_event_and_type[0] === "milestone") {
      return taskOptions;
    }

    const currentContingentEvent = [
      {
        value: task.contingency_event_and_type.join("-"),
        label: startCase(task.parent_task_name || task.contingency_event_and_type[1]),
      },
    ];
    return currentContingentEvent.concat(taskOptions);
  };

  const taskTypeOptionValues = useMemo(() => {
    const taskTypes = [
      "To-Do",
      !taskable ? "Appointment" : null,
      "Call",
      "Text",
      "Email",
      "Mail",
      "Social Media",
      "Door Knock",
    ];

    return taskTypes.filter(Boolean).map((taskType) => ({ value: taskType, label: taskType }));
  }, [taskable]);

  const taskForWatcher = (result) => {
    const taskableName = result.label?.split(" ").slice(0, 2).join(" ") || null;
    const taskableEmail = result.label?.split(" ").slice(2).join(" ") || null;
    patchTask({
      taskable_type: result.object_type,
      taskable_id: result.uuid,
      taskable_name: taskableName,
      taskable_email: taskableEmail,
    });
  };

  useEffect(() => {
    if (!cachedAgentPromise) {
      cachedAgentPromise = caseTransformingAxios.get("/simple_account_active_agents").then((response) =>
        [{ label: "Anyone", value: "anyone" }].concat(
          response.data.map((agent) => ({
            label: agent.fullName,
            value: agent.uuid,
            email: agent.email,
          })),
        ),
      );
    }

    cachedAgentPromise.then((activeAgents) => {
      setAgents(activeAgents);
    });
  }, []);

  const assistantTaskChangeHandler = externalTaskChange || handleTaskChange;
  const currentDueDayjs = dayjs(task.due_on, "YYYY-MM-DD", true);
  const currentTaskTime = task.scheduled_time ? dayjs.unix(task.scheduled_time).format("hh:mm A") : null;
  const selectedEasyTimeKey = currentTaskTime ? TASK_EASY_TIME_VALUE_TO_KEY[currentTaskTime] || null : "any";
  const isCustomTimeSelected = Boolean(currentTaskTime) && !selectedEasyTimeKey;
  const customTimePillLabel = isCustomTimeSelected ? currentTaskTime : "Custom";
  const isTaskTimeDisabled = taskDetails || !currentDueDayjs.isValid();
  const timeEasyOptions =
    task.type === "Appointment"
      ? TASK_EASY_TIME_OPTIONS.filter((option) => option.key !== "any")
      : TASK_EASY_TIME_OPTIONS;
  const selectedTaskTimeOption = TASK_TIME_OPTIONS.find((option) => option.value === currentTaskTime) || null;
  const matchesPreset = (daysFromNow) =>
    currentDueDayjs.isValid() && currentDueDayjs.isSame(dayjs().add(daysFromNow, "day"), "day");
  const isCustomDateSelected =
    currentDueDayjs.isValid() && !DATE_PRESET_OPTIONS.some(({ daysFromNow }) => matchesPreset(daysFromNow));
  const customDatePillLabel =
    isCustomDateSelected && currentDueDayjs.isValid() ? currentDueDayjs.format("MM/DD/YY") : "Custom";
  const handleDatePresetSelect = (daysFromNow) => {
    if (showContingency) {
      toggleContingent();
    }
    setIsCustomDatePickerOpen(false);
    const targetDate = dayjs().add(daysFromNow, "day").format("MM/DD/YYYY");
    handleDueOnChange(targetDate);
  };
  const handleCustomDateSelect = (formattedDate, shouldClosePicker = true) => {
    if (!formattedDate) return;
    if (showContingency) {
      toggleContingent();
    }
    handleDueOnChange(formattedDate);
    if (shouldClosePicker) setIsCustomDatePickerOpen(false);
  };

  const handleCustomDateSelectorChange = (dateSelectorState = {}) => {
    const { date, formattedDate } = dateSelectorState;

    const isValidDate = Boolean(formattedDate) && dayjs(formattedDate, "MM/DD/YYYY", true).isValid();
    if (!isValidDate || date !== formattedDate) return;

    handleCustomDateSelect(formattedDate, dateSelectorState.dateChangeMethod !== "typed");
  };

  const appointmentTypeOptionGroups = APPOINTMENT_TYPE_OPTION_GROUPS;
  const flatAppointmentOptions = appointmentTypeOptionGroups.reduce(
    (acc, group) => [...acc, ...group.options],
    [],
  );
  const contingencyEventOptions = taskableOptions(task.taskable_type);
  const selectedRecurringOption =
    RECURRING_OPTIONS.find((option) => option.key === selectedRecurringOptionKey) || RECURRING_OPTIONS[0];
  const shouldShowRecurringDaysInput = selectedRecurringOption?.key === "custom";

  return (
    <>
      {showHeader && <Modal.Header title={getTaskTitle({ task })} closeButton />}
      <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
        <form
          className={`task-form tw-flex-1 tw-flex tw-flex-col tw-gap-[24px] tw-overflow-auto ${isEmbedded ? "tw-p-[16px]" : ""}`}
        >
          {errors && (
            <div className="task-errors">
              <Alert
                variant="error"
                text={
                  <dl className="tw-mb-0">
                    {errors.map((error) => (
                      <dd key={error}> {error} </dd>
                    ))}
                  </dl>
                }
              />
            </div>
          )}

          <TextInput
            label="Task Details (max 255 characters)"
            isRequired
            name="name"
            onChange={handleTaskChange}
            value={task.name}
            maxLength={255}
            disabled={taskDetails}
          />

          <div className="tw-flex tw-flex-wrap tw-gap-[16px]">
            {!isCreatingAppointment && !(task.id && task.type === "Appointment") && (
              <Dropdown
                containerClassName={`tw-flex-1 ${task.type === "Appointment" || !showTaskFor ? "tw-min-w-[40%]" : "tw-min-w-[100%]"}`}
                label="Type"
                isRequired
                menuShouldComeToFront
                options={taskTypeOptionValues}
                name="type"
                onChange={(option, handler) =>
                  handleTaskChange({ target: { name: handler.name, value: option.value } })
                }
                value={taskTypeOptionValues.find((opt) => opt.value === task.type) || taskTypeOptionValues[0]}
                isDisabled={editTaskFor || taskDetails}
              />
            )}

            {task.type === "Appointment" && (
              <Dropdown
                containerClassName="tw-flex-1 tw-min-w-[40%]"
                menuShouldComeToFront
                label="Appointment Type"
                isRequired
                name="appointment_type"
                onChange={(option) => patchTask({ appointment_type: option.value })}
                value={flatAppointmentOptions.find((opt) => opt.value === task.appointment_type) || null}
                options={appointmentTypeOptionGroups}
                isDisabled={taskDetails}
              />
            )}

            <Dropdown
              containerClassName="tw-flex-1 tw-min-w-[40%]"
              menuShouldComeToFront
              label="Assigned To"
              isSearchable
              name="assigned_to_user_id"
              onChange={(agent, handler) =>
                handleTaskChange({ target: { name: handler?.name, value: agent?.value } })
              }
              isClearable={false}
              value={agents?.find((agent) => agent.value === task.assigned_to_user_id) || agents?.[0] || null}
              options={agents}
              isDisabled={taskDetails}
              isLoading={!agents?.length}
            />

            {showTaskFor && !isCreatingAppointment && (
              <div className="tw-flex-1 tw-min-w-[40%]">
                <TaskFor
                  id="task-for"
                  onChange={taskForWatcher}
                  taskFor={taskFor}
                  isAppointment={task.type === "Appointment"}
                />
              </div>
            )}
          </div>

          {task.taskable_type === "Listing" && (
            <Checkbox
              label="Hide from client portal viewers"
              name="externally_visible"
              checked={!task.externally_visible}
              onChange={(e) =>
                handleTaskChange({ target: { name: e.target.name, value: !e.target.checked } })
              }
            />
          )}

          {task.type === "Appointment" && (
            <>
              <TextInput
                label="Location"
                onChange={(e) => {
                  const { value } = e.target;
                  patchTask({ location: value });
                }}
                defaultValue={task.location}
                name="location"
              />
              {!task.completed && (
                <Toggle
                  text="Add to Google Calendar"
                  onChange={() => setAddTo((prevAddTo) => !prevAddTo)}
                  checked={addTo}
                />
              )}
              <OutcomeSelection
                task={task}
                handleOutcomeOnChange={(appointmentOutcome) => patchTask({ outcome: appointmentOutcome })}
              />
            </>
          )}

          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <FieldLabel label={taskDetails ? "Due Date" : "Date"} isRequired />
              <div className="tw-flex tw-flex-wrap tw-gap-[8px]">
                {DATE_PRESET_OPTIONS.map(({ label, daysFromNow }) => (
                  <Tooltip
                    trigger={
                      <FilterPill
                        key={label}
                        size="medium"
                        variant="primary"
                        onClick={() => handleDatePresetSelect(daysFromNow)}
                        selected={matchesPreset(daysFromNow)}
                        disabled={taskDetails}
                      >
                        {label}
                      </FilterPill>
                    }
                    content={dayjs().add(daysFromNow, "day").format("dddd, MMM D")}
                  />
                ))}
                <Popover
                  placement="bottom-end"
                  open={isCustomDatePickerOpen}
                  onOpenChange={(isOpen) => {
                    if (taskDetails) return;
                    if (isOpen && task.type !== "Appointment" && showContingency) {
                      toggleContingent();
                    }
                    setIsCustomDatePickerOpen(isOpen);
                  }}
                >
                  <PopoverTrigger>
                    <div>
                      <FilterPill
                        size="medium"
                        variant="primary"
                        onClick={() => setIsCustomDatePickerOpen(true)}
                        selected={isCustomDateSelected || isCustomDatePickerOpen}
                        disabled={taskDetails}
                      >
                        <span className="tw-inline-flex tw-items-center tw-gap-[6px]">
                          {customDatePillLabel}
                          <CalendarDaysSolidV6 size="l" />
                        </span>
                      </FilterPill>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="tw-relative">
                    <DateSelector
                      value={currentDueDayjs.isValid() ? currentDueDayjs.format("MM/DD/YYYY") : ""}
                      onChange={handleCustomDateSelectorChange}
                    />
                  </PopoverContent>
                </Popover>
                {task.type !== "Appointment" && (
                  <FilterPill
                    size="medium"
                    variant="primary"
                    onClick={toggleContingent}
                    selected={showContingency}
                    disabled={taskDetails}
                  >
                    Contingent
                  </FilterPill>
                )}
              </div>
            </div>
          </div>

          {(task.type === "Appointment" || !showContingency) && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <div className="tw-flex tw-flex-col tw-gap-[8px]">
                <FieldLabel label="Time" isRequired={task.type === "Appointment"} />
                <div className="tw-flex tw-flex-wrap tw-gap-[8px]">
                  {timeEasyOptions.map((timeOption) => (
                    <FilterPill
                      key={timeOption.key}
                      size="medium"
                      variant="primary"
                      onClick={() => handleEasyTimeSelect(timeOption.value)}
                      selected={selectedEasyTimeKey === timeOption.key}
                      disabled={isTaskTimeDisabled}
                    >
                      <span className="tw-inline-flex tw-items-center tw-gap-[6px]">
                        <span>{timeOption.label}</span>
                        {timeOption.subLabel && (
                          <span className="tw-text-neutral-gray-50">{timeOption.subLabel}</span>
                        )}
                      </span>
                    </FilterPill>
                  ))}
                  <Dropdown
                    className="tw-w-fit"
                    options={TASK_TIME_OPTIONS}
                    value={selectedTaskTimeOption}
                    placeholder="Custom"
                    isSearchable={false}
                    menuShouldComeToFront
                    isDisabled={isTaskTimeDisabled}
                    onChange={handleTaskTimeChange}
                    customPillLabel={customTimePillLabel}
                    isCustomTimeSelected={isCustomTimeSelected}
                    components={{
                      Control: TaskTimePillControl,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {task.type !== "Appointment" && showContingency && (
            <div className="tw-flex tw-gap-[16px]">
              <div className="tw-flex-1 tw-flex tw-gap-[16px]">
                <TextInput
                  isRequired
                  containerClassName="tw-flex-1"
                  size="1"
                  label="# of days"
                  name="days"
                  onChange={handleContingencyRuleChange}
                  value={task.contingency_rule.days || ""}
                />
                <div className="tw-flex-1 tw-flex tw-items-end">
                  <TextToggle
                    options={CONTINGENCY_OPTIONS}
                    value={task.contingency_rule.position}
                    onChange={(e) =>
                      handleContingencyRuleChange({
                        target: { name: "position", value: e.target.value },
                      })
                    }
                  />
                </div>
              </div>
              <Dropdown
                isRequired
                menuShouldComeToFront
                containerClassName="tw-flex-1"
                label="Event"
                name="event"
                onChange={(option, handler) =>
                  handleContingencyRuleChange({
                    target: { name: handler.name, value: option.value },
                  })
                }
                value={contingencyEventOptions?.find((opt) => opt.value === task.contingency_rule.event)}
                options={contingencyEventOptions}
              />
            </div>
          )}

          {task.type !== "Appointment" && !hideRecurring && (
            <>
              <Toggle
                labelClassName="tw-self-start"
                text="Recurring"
                checked={showRecurring}
                onChange={toggleRecurring}
              />
              {showRecurring && (
                <div className="tw-flex tw-gap-[16px]">
                  <div className="tw-flex-1 tw-flex tw-gap-[16px]">
                    <Dropdown
                      menuShouldComeToFront
                      containerClassName="tw-flex-1"
                      label="Recurring"
                      name="recurring_frequency"
                      onChange={handleRecurringFrequencyChange}
                      value={selectedRecurringOption}
                      options={RECURRING_OPTIONS}
                      getOptionValue={(option) => option.key}
                    />
                    {shouldShowRecurringDaysInput && (
                      <TextInput
                        containerClassName="tw-flex-1"
                        isRequired
                        size="1"
                        label="# of days"
                        onChange={handleTaskChange}
                        name="recurring_every_so_many_days"
                        value={task.recurring_every_so_many_days || ""}
                      />
                    )}
                    {task.recurring_frequency === "weekly" && (
                      <Dropdown
                        menuShouldComeToFront
                        containerClassName="tw-flex-1"
                        label="Day of Week"
                        isSearchable
                        isClearable={false}
                        name="recurring_day_of_week"
                        onChange={(option, handler) =>
                          handleTaskChange({ target: { name: handler.name, value: option.value } })
                        }
                        value={RECURRING_DOW_OPTIONS.find((opt) => opt.value === task.recurring_day_of_week)}
                        options={RECURRING_DOW_OPTIONS}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <FieldLabel label="Priority (1: highest, 9: lowest)" isRequired />
            <div className="priority-buttons tw-max-w-[690px] tw-flex tw-flex-wrap tw-gap-[8px]">
              {PRIORITY_OPTIONS.map((priorityNumber) => (
                <FilterPill
                  key={priorityNumber}
                  variant="primary"
                  containerClassName="tw-flex-1"
                  labelClassName="tw-w-full tw-text-center"
                  onClick={() => patchTask({ priority: priorityNumber })}
                  selected={task.priority === priorityNumber}
                >
                  {priorityNumber}
                </FilterPill>
              ))}
            </div>
          </div>

          {task.showInstructions && (
            <TextInput
              id="task-instructions-input"
              multiline
              size="auto"
              name="instructions"
              label={
                <div className="tw-w-full tw-flex tw-justify-between tw-items-end tw-gap-[8px]">
                  <span>Instructions</span>
                  <div className="tw-flex tw-items-center tw-gap-[8px]">
                    <AiAssistant
                      textareaId="task-instructions-input"
                      messageType="Generic"
                      onInsertClick={(text) =>
                        assistantTaskChangeHandler({
                          target: { name: "instructions", value: text },
                        })
                      }
                    />
                    <IconButton size="small" schema="transparent" onClick={toggleInstructions}>
                      <XmarkSolidV6 />
                    </IconButton>
                  </div>
                </div>
              }
              value={task.instructions || ""}
              onChange={handleTaskChange}
            />
          )}

          {task.showNotes && (
            <TextInput
              multiline
              size="auto"
              name="note"
              label={
                <div className="tw-w-full tw-flex tw-justify-between tw-items-end tw-gap-[8px]">
                  <span>Notes</span>
                  <IconButton size="small" schema="transparent" onClick={toggleNotes}>
                    <XmarkSolidV6 />
                  </IconButton>
                </div>
              }
              value={task.note || ""}
              onChange={handleTaskChange}
            />
          )}

          {(!task.showInstructions || !task.showNotes) && (
            <div className="tw-flex tw-gap-[12px]">
              {!task.showInstructions && (
                <Button onClick={toggleInstructions}>
                  <Plus size="s" className="tw-mr-[4px]" />
                  Add Instructions
                </Button>
              )}
              {!task.showNotes && (
                <Button onClick={toggleNotes}>
                  <Plus size="s" className="tw-mr-[4px]" />
                  Add Notes
                </Button>
              )}
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer
        className={`tw-flex tw-justify-between tw-items-center ${isEmbedded ? "tw-border-x-0 tw-border-b-0 tw-border-solid tw-border-[1px] tw-border-gray-10 tw-px-[16px] tw-py-[16px] tw-pb-[12px]" : ""}`}
      >
        {hideCancel ? (
          <span />
        ) : (
          <Button
            data-dismiss="modal"
            onClick={onClose}
            size={isEmbedded ? "small" : "medium"}
            schema="tertiary"
          >
            Cancel
          </Button>
        )}

        <div className="tw-flex tw-gap-[8px]">
          {!isEmbedded && (
            <Checkbox
              checked={task.completed}
              label="Mark As Complete"
              onClick={toggleCompleteTask}
              disabled={disableCompletedCheckbox || task.state === "completed"}
            />
          )}
          {/* btn will be disabled when its creating or scheduling appointment from PDP/ contact index page,
            task name, priority, task type, appointment type and due_on date is required field for appointment */}
          <Button
            isLoading={disabledButton}
            disabled={
              !task.name.length ||
              (task.type === "Appointment" &&
                (!task.appointment_type || !task.due_on || (task.completed && !task.outcome)))
            }
            size={isEmbedded ? "small" : "medium"}
            schema={isEmbedded ? "secondary" : "primary"}
            onClick={handleSubmit}
          >
            {getTaskButtonText({ task, isEmbedded })}
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
};

TaskForm.propTypes = {
  agreementCallBack: PropTypes.func,
  contactEmail: PropTypes.string,
  editTaskFor: PropTypes.bool,
  getCardTasks: PropTypes.func,
  handleTaskChange: PropTypes.func,
  hideRecurring: PropTypes.bool,
  hideCancel: PropTypes.bool,
  isCreatingAppointment: PropTypes.bool,
  listingUuid: PropTypes.string,
  onClose: PropTypes.func,
  person: PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    data: PropTypes.shape({
      attributes: PropTypes.shape({
        uuid: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
      }),
    }),
  }),
  showHeader: PropTypes.bool,
  showTaskFor: PropTypes.bool,
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    assigned_to_user_id: PropTypes.string,
    completed: PropTypes.bool,
    contingent: PropTypes.bool,
    instructions: PropTypes.string,
    location: PropTypes.string,
    note: PropTypes.string,
    outcome: PropTypes.string,
    priority: PropTypes.number,
    recurring: PropTypes.bool,
    type: PropTypes.string,
    user_id: PropTypes.string,
  }),
  taskDetails: PropTypes.bool,
  taskFor: PropTypes.shape({
    uuid: PropTypes.string,
    object_type: PropTypes.string,
    name: PropTypes.string,
  }),
  taskable: PropTypes.shape({
    appointment_type: PropTypes.string,
    uuid: PropTypes.string,
    taskable_type: PropTypes.string,
    task_name: PropTypes.string,
    outcome: PropTypes.string,
    location: PropTypes.string,
  }),
  updateTask: PropTypes.func,
  v4TaskApi: PropTypes.bool,
  isEmbedded: PropTypes.bool,
};

export default TaskForm;
